/**
 * A2A Client Configuration for VocabRight
 * 
 * Initializes the A2A client for communicating with the A2AEnglishAI backend server.
 * This client handles all A2A protocol communication including:
 * - Capability discovery
 * - Task execution
 * - UX negotiation
 * - Message signing and authentication
 */

import { A2AClient } from '../shared/a2a-client.js';

// A2A Backend Server URLs
const LOCALHOST_URL = 'http://localhost:8787';
const PRODUCTION_URL = 'https://a2aenglishai-server.appsokay.net';

// Get initial URL from localStorage or environment variable
// Default to PRODUCTION (Cloudflare) for deployed apps
const getInitialUrl = () => {
  // Detect if we're on Cloudflare/production
  const isProduction = typeof window !== 'undefined' && 
                       window.location.hostname !== 'localhost' && 
                       window.location.hostname !== '127.0.0.1' &&
                       !window.location.hostname.includes('localhost');
  
  // If on production, force production URL (ignore localStorage)
  if (isProduction) {
    // Clear any localhost preference
    if (typeof window !== 'undefined') {
      localStorage.setItem('a2a_use_localhost', 'false');
    }
    return PRODUCTION_URL;
  }
  
  // For localhost, check localStorage first (set by toggle)
  // Default to PRODUCTION_URL (not localhost) to avoid connection errors
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('a2a_use_localhost');
    if (saved === 'true') {
      return LOCALHOST_URL;
    }
    // If saved is 'false' or not set, use production
    return PRODUCTION_URL;
  }
  
  // Fallback to environment variable or production (default to production)
  return process.env.REACT_APP_A2A_BACKEND_URL || PRODUCTION_URL;
};

const REMOTE_AGENT_URL = getInitialUrl();

// Client Agent ID
const AGENT_ID = 'vocabright-client-001';

// A2A Secret Key (optional, for message signing)
// Load from environment variable if available
const SECRET_KEY = process.env.REACT_APP_A2A_SECRET_KEY || null;

// Always log (even in production) for debugging
console.log('[A2A Client Config]', {
  backendURL: REMOTE_AGENT_URL,
  agentID: AGENT_ID,
  hasSecretKey: !!SECRET_KEY,
  isLocalhost: REMOTE_AGENT_URL.includes('localhost'),
  note: 'Use the A2A Server toggle (bottom left) to switch between localhost and production'
});

/**
 * Initialize A2A Client
 */
const vocabRightClient = new A2AClient(REMOTE_AGENT_URL, AGENT_ID, {
  secretKey: SECRET_KEY,
  timeout: 30000,       // 30 second timeout
  maxRetries: 3,        // Retry failed requests up to 3 times
  enableLogging: process.env.NODE_ENV === 'development', // Enable logging in development
});

/**
 * Update A2A server URL dynamically
 * Called by the A2A Server toggle component
 */
export const updateA2AServerUrl = (newUrl) => {
  // Cancel any pending initialization first
  if (initializationPromise) {
    initializationPromise = null;
  }
  
  // Update the URL first
  vocabRightClient.updateRemoteUrl(newUrl);
  
  // Clear cached capabilities to force rediscovery
  vocabRightClient.clearCache();
  capabilitiesInitialized = false;
  
  console.log(`[A2A Client] Server URL updated to: ${newUrl}`);
  
  // Re-initialize capabilities with new URL
  if (vocabRightClient.remoteUrl === newUrl) {
    initializeCapabilities().catch(error => {
      const isConnectionError = error.message && (
        error.message.includes('Failed to fetch') ||
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('NETWORK_ERROR')
      );
      if (!isConnectionError) {
        console.warn('[A2A Client] Failed to reinitialize with new URL:', error.message);
      }
    });
  }
};

// Expose to window for toggle component
if (typeof window !== 'undefined') {
  window.updateA2AServerUrl = updateA2AServerUrl;
}

/**
 * Auto-initialize capabilities on module load
 */
let capabilitiesInitialized = false;
let initializationPromise = null;

export const initializeCapabilities = async () => {
  if (capabilitiesInitialized) {
    return vocabRightClient.getCapabilities();
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    try {
      console.log('[A2A Client] Discovering capabilities from backend...');
      const capabilities = await vocabRightClient.discoverCapabilities();
      capabilitiesInitialized = true;
      console.log(`[A2A Client] ✓ Capability discovery successful: Found ${capabilities.length} capabilities from ${vocabRightClient.remoteUrl}`);
      return capabilities;
    } catch (error) {
      const isConnectionError = error.message && (
        error.message.includes('Failed to fetch') ||
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('NETWORK_ERROR')
      );
      
      if (!isConnectionError) {
        console.error('[A2A Client] Failed to discover capabilities:', error);
      }
      throw error;
    }
  })();

  return initializationPromise;
};

// Auto-initialize capabilities when the module is loaded
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const currentUrl = vocabRightClient.remoteUrl;
    const expectedUrl = getInitialUrl();
    
    if (currentUrl === expectedUrl) {
      initializeCapabilities().catch(error => {
        const isConnectionError = error.message && (
          error.message.includes('Failed to fetch') ||
          error.message.includes('ERR_CONNECTION_REFUSED') ||
          error.message.includes('NETWORK_ERROR')
        );
        
        if (!isConnectionError || process.env.NODE_ENV === 'development') {
          console.warn('[A2A Client] Auto-initialization failed (will use fallback):', error.message);
        }
      });
    }
  }, 2000);
}

/**
 * Helper functions for common operations
 */
export const executeTask = async (capabilityName, parameters = {}, options = {}) => {
  return await vocabRightClient.executeTask(capabilityName, parameters, options);
};

export const getCapabilities = () => {
  return vocabRightClient.getCapabilities();
};

export const negotiateUX = async (context = null) => {
  const userContext = context || vocabRightClient.detectContext();
  return await vocabRightClient.negotiateUX(userContext);
};

export const detectContext = () => {
  return vocabRightClient.detectContext();
};

/**
 * Export the client instance as default
 */
export default vocabRightClient;

